import { ref } from 'vue'
import { createNamespacedHelpers } from 'vuex-composition-helpers'
import { generatePartnerSsoToken, getAuthXToken, getLocalUserDeviceToken, parseRedirectUri } from '@/services/auth'
import { nrRedirectToProduct } from '@/services/newrelic'
import { Provider } from '@/types'
import { router } from '@/router'
import { getDeviceKey } from '@/services/trusted-device/trusted-device-store'
import { isTrustedDeviceEnrollmentEnabled } from '@/services/trusted-device/is-trusted-device-enrollment-enabled'
import { useVerifyTrustedDeviceChallenge } from '@/services/trusted-device/use-verify-trusted-device-challenge'
import { determineRedirectUri } from './determine-redirect-uri'
import { modifyUrlQuery } from './modify-url-query'

const { useState: useUserStoreState, useActions: useUserStoreActions } = createNamespacedHelpers('User')
const { useState: useLoginDomainStoreState } = createNamespacedHelpers('LoginDomain')

/**
 * Completes login by resolving the redirect target for the authenticated user
 * and navigating to it (product redirect, SSO hand-off, or an interstitial
 * error/warning page). Shared by AuthenticatedPage and the trusted-device
 * success screen so both drive the exact same redirect behaviour.
 *
 * Must be called from within a component's setup().
 */
export const useCompleteLogin = () => {

  const { userIdentity, trustedDeviceChallengeId } = useUserStoreState(['userIdentity', 'trustedDeviceChallengeId'])
  const { authorizeRedirectUri } = useUserStoreActions(['authorizeRedirectUri'])
  const { tenant } = useLoginDomainStoreState(['tenant'])
  const { verify } = useVerifyTrustedDeviceChallenge()
  // Set when the user must verify their email before continuing — the caller
  // is responsible for rendering the verification UI in that case.
  const emailVerificationRequired = ref(false)

  const completeLogin = async () => {

    try {

      if (!userIdentity.value) {
        router.replace({ name: 'Root' })
        return
      }

      const token = getAuthXToken()

      if (userIdentity.value?.emailVerificationRequired) {
        emailVerificationRequired.value = true
        return
      }

      if (userIdentity.value && !trustedDeviceChallengeId.value && isTrustedDeviceEnrollmentEnabled(userIdentity.value.email, token.payload.partnerCode)) {
        const device = await getDeviceKey(userIdentity.value.email).catch(() => null)
        if (device) {
          await verify(device.deviceId, device.privateKey, token.value).catch(() => null)
        }
      }

      const defaultRedirectUri = determineRedirectUri(userIdentity.value?.defaultRedirectUri)

      const userDeviceToken = getLocalUserDeviceToken()

      const deviceIdParams = trustedDeviceChallengeId.value
        ? { authxDeviceChallengeId: trustedDeviceChallengeId.value }
        : {}

      const redirectUri = userDeviceToken
        ? modifyUrlQuery(defaultRedirectUri, { userDeviceToken, ...deviceIdParams })
        : modifyUrlQuery(defaultRedirectUri, deviceIdParams)

      if (userIdentity.value?.passwordExpiryInDays <= userIdentity.value?.passwordChangeWarningInDays) {
        // The user's password will expire within the threshold.
        // Note: The 'threshold' is a measurement determined downstream
        if (userIdentity.value?.passwordExpiryInDays <= 0) {
          // The user's password has expired and cannot be used
          await router.push({ name: 'PasswordExpiredErrorPage' })
          return
        }
        await router.push({
          name: 'PasswordWillExpireErrorPage',
          query: {
            passwordExpiryInDays: String(userIdentity.value?.passwordExpiryInDays),
            redirectUri,
          },
        })
        return
      }

      if (token.payload.provider === Provider.authx) {

        await generatePartnerSsoToken(token.value, token.payload.partnerCode)
          .then(({ token: ssoToken, tenantCode }) => {
            nrRedirectToProduct()
            window.location.assign(parseRedirectUri(redirectUri, tenantCode, ssoToken))
          })

      } else {

        await authorizeRedirectUri({
          tenant: tenant.value,
          redirectUri,
        })

      }

    } catch {

      router.replace({ name: 'UnknownErrorPage' })

    }

  }

  return {
    completeLogin,
    emailVerificationRequired,
    userIdentity,
    tenant,
  }

}
